import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { localBackend, type InspectionRecord, type UserProfile, DefectType, Severity } from '../services/localBackend';

export { DefectType, Severity } from '../services/localBackend';
export type { InspectionRecord, UserProfile } from '../services/localBackend';

export function useGetCallerUserProfile() {
  const query = useQuery<UserProfile | null>({
    queryKey: ['currentUserProfile'],
    queryFn: () => localBackend.getCallerUserProfile(),
    retry: false,
  });

  return query;
}

export function useSaveCallerUserProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (profile: UserProfile) => {
      localBackend.saveCallerUserProfile(profile);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentUserProfile'] });
    },
  });
}

export function useGetKPIs() {
  return useQuery({
    queryKey: ['kpis'],
    queryFn: () => localBackend.getKPIs(),
    refetchInterval: 30000,
  });
}

export function useGetRecentInspections(count: number = 10) {
  return useQuery({
    queryKey: ['recentInspections', count],
    queryFn: () => localBackend.getRecentInspections(count),
    refetchInterval: 15000,
  });
}

export function useGetAllInspectionRecords() {
  return useQuery({
    queryKey: ['allInspectionRecords'],
    queryFn: () => localBackend.getAllInspectionRecords(),
  });
}

export function useGetInspectionTrends() {
  return useQuery({
    queryKey: ['inspectionTrends'],
    queryFn: () => localBackend.getInspectionTrends(),
    refetchInterval: 30000,
  });
}

export function useGetDefectDistribution() {
  return useQuery({
    queryKey: ['defectDistribution'],
    queryFn: () => localBackend.getDefectDistribution(),
    refetchInterval: 30000,
  });
}

export function useGetSeverityDistribution() {
  return useQuery({
    queryKey: ['severityDistribution'],
    queryFn: () => localBackend.getSeverityDistribution(),
    refetchInterval: 30000,
  });
}

export function useGetClassificationsStats() {
  return useQuery({
    queryKey: ['classificationsStats'],
    queryFn: () => localBackend.getClassificationsStats(),
    refetchInterval: 30000,
  });
}

export function useGetTodayStats() {
  return useQuery({
    queryKey: ['todayStats'],
    queryFn: () => localBackend.getTodayStats(),
    refetchInterval: 10000,
  });
}

export function useAddInspectionRecord() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (record: Omit<InspectionRecord, 'id'> & { id?: number }) => {
      localBackend.addInspectionRecord(record);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kpis'] });
      queryClient.invalidateQueries({ queryKey: ['recentInspections'] });
      queryClient.invalidateQueries({ queryKey: ['allInspectionRecords'] });
      queryClient.invalidateQueries({ queryKey: ['inspectionTrends'] });
      queryClient.invalidateQueries({ queryKey: ['defectDistribution'] });
      queryClient.invalidateQueries({ queryKey: ['severityDistribution'] });
      queryClient.invalidateQueries({ queryKey: ['classificationsStats'] });
    },
  });
}

export function useDeleteInspectionRecord() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      localBackend.deleteInspectionRecord(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allInspectionRecords'] });
      queryClient.invalidateQueries({ queryKey: ['recentInspections'] });
      queryClient.invalidateQueries({ queryKey: ['kpis'] });
    },
  });
}

// Helper functions for display
export function getDefectTypeLabel(defectType: DefectType): string {
  switch (defectType) {
    case DefectType.scratch: return 'Scratch';
    case DefectType.crack: return 'Crack';
    case DefectType.warp: return 'Warp';
    case DefectType.discoloration: return 'Discoloration';
    default: return 'Unknown';
  }
}

export function getSeverityLabel(severity: Severity): string {
  switch (severity) {
    case Severity.good: return 'Good';
    case Severity.minorDefect: return 'Minor Defect';
    case Severity.majorDefect: return 'Major Defect';
    default: return 'Unknown';
  }
}

export function getSeverityColor(severity: Severity): string {
  switch (severity) {
    case Severity.good: return 'text-success';
    case Severity.minorDefect: return 'text-warning';
    case Severity.majorDefect: return 'text-danger';
    default: return 'text-muted-foreground';
  }
}

export function getSeverityBadgeClass(severity: Severity): string {
  switch (severity) {
    case Severity.good: return 'bg-status-success/20 text-status-success border border-status-success/30';
    case Severity.minorDefect: return 'bg-status-warning/20 text-status-warning border border-status-warning/30';
    case Severity.majorDefect: return 'bg-status-danger/20 text-status-danger border border-status-danger/30';
    default: return 'bg-muted text-muted-foreground';
  }
}
